import { SearchOutlined } from '@ant-design/icons';
import { Checkbox, Input, InputRef, Modal, notification } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { UserUSIResponse } from 'server/src/types/user';
import { trpc } from '../../../trpc';

interface Props {
    open: boolean;
    onClose: () => void;
}

const accessToken = new URLSearchParams(window.location.search).get('accessToken');

const ExemptedUserList: React.FC<Props> = ({ open, onClose }) => {
    const [search, setSearch] = useState<string>('');
    const [exemptedUsers, setExemptedUsers] = useState<number[]>([]);
    const [nonExemptedUsers, setNonExemptedUsers] = useState<(UserUSIResponse & { avatarUrl?: string })[]>([]);
    const { data: allUser } = trpc.User.getAllUser.useQuery();
    const { data: listExemptedUser } = trpc.User.getExemptedUsers.useQuery();
    const updateExemptedUser = trpc.User.updateExemptedUsers.useMutation();
    const inputRef = useRef<InputRef>(null);

    useEffect(() => {
        if (listExemptedUser) {
            setExemptedUsers(listExemptedUser.map((user) => user.userId));
        }

        if (allUser) {
            setNonExemptedUsers(allUser?.filter(
                (user) => !listExemptedUser?.map(u => u.userId).includes(user.id)))
        }
    }, [listExemptedUser, allUser]);

    // Xử lý click bên ngoài Input
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (inputRef.current && !inputRef.current.input?.contains(event.target as Node)) {
                inputRef.current.blur(); // Mất focus khi click bên ngoài
            }
        };

        // Thêm sự kiện lắng nghe khi Modal mở
        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        // Dọn dẹp sự kiện khi Modal đóng hoặc component unmount
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [open]);

    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(event.target.value);
    };

    const handleRemoveExemptedUser = (userId: number) => {
        setExemptedUsers((prev) => prev.filter((id) => id !== userId));
        setNonExemptedUsers((prev) => {
            const foundUser = allUser?.find(user => user.id === userId);
            if (foundUser && !prev.some(user => user.id === foundUser.id)) {
                return [...prev, foundUser];
            }
            return prev;
        });
    };

    const handleCheckboxChange = (userId: number, checked: boolean) => {
        setExemptedUsers((prev) => {
            if (checked) {
                return [...prev, userId];
            } else {
                return prev.filter((id) => id !== userId);
            }
        });
    };

    const handleSave = async () => {
        await updateExemptedUser.mutateAsync({ exemptedUserIds: exemptedUsers });
        notification.success({ message: '動作免除一覧の更新が完了しました。' })
        onClose();
    };

    return (
        <div
            onDoubleClick={(e: React.MouseEvent) => e.stopPropagation()}
            onWheel={(e: React.WheelEvent) => e.stopPropagation()}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
            <Modal
                title="動作免除一覧"
                cancelText='キャンセル'
                okText='保存'
                centered
                open={open}
                onOk={handleSave}
                onCancel={onClose}
            >
                <div className="flex flex-col gap-4">
                    <div className="flex gap-3 overflow-auto w-full">
                        {listExemptedUser?.map(u => u.userId)
                            .filter(userId => exemptedUsers.includes(userId)
                                && !nonExemptedUsers.map(u => u.id).includes(userId))
                            .map((userId) => {
                                const userInfo = allUser?.find((u) => u.id === userId);
                                const avatarUrl = userInfo?.avatarUrl
                                    ? `${userInfo.avatarUrl}&token=${accessToken}`
                                    : 'user1.jpg';
                                const name = userInfo?.name;

                                return (
                                    <div key={userId} className="relative w-[70px] h-[105px]">
                                        <label
                                            className="absolute right-1 top-0 rounded-full border border-gray-300 bg-white h-[20px] w-[20px] flex justify-center items-center z-50 cursor-pointer"
                                            onClick={() => handleRemoveExemptedUser(userId)}
                                        >
                                            ✕
                                        </label>
                                        <img
                                            className="w-[70px] h-[70px] border rounded-full object-cover"
                                            src={avatarUrl}
                                            alt={name}
                                        />
                                        <p className="w-[70px] truncate text-center">{name}</p>
                                    </div>
                                );
                            })}
                    </div>

                    {/* Input với ref để quản lý focus */}
                    <Input
                        ref={inputRef}
                        placeholder="検索"
                        size="large"
                        prefix={<SearchOutlined style={{ color: '#AEADAD', fontSize: '20px' }} />}
                        className="rounded-full"
                        style={{
                            borderRadius: '999px',
                            boxShadow: 'none'
                        }}
                        value={search}
                        onChange={handleSearchChange}
                        allowClear
                    />

                    <div className="flex flex-col gap-3 overflow-auto h-[300px]">
                        {nonExemptedUsers
                            ?.filter((user) => user.name.toLowerCase().includes(search.toLowerCase()))
                            .map((user) => (
                                <div
                                    key={user.id}
                                    className="flex items-center justify-between p-2"
                                >
                                    <div className="flex items-center gap-3">
                                        <img
                                            className="w-[60px] h-[60px] border rounded-full object-cover"
                                            src={user.avatarUrl ? `${user.avatarUrl}&token=${accessToken}` : 'user1.jpg'}
                                            alt={user.name}
                                        />
                                        <span className="text-sm truncate">{user.name}</span>
                                    </div>
                                    <Checkbox
                                        checked={exemptedUsers.includes(user.id)}
                                        onChange={(e) => handleCheckboxChange(user.id, e.target.checked)}
                                    />
                                </div>
                            ))}
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default ExemptedUserList;